import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithClient } from "@/redux/baseQuery";
import {
  FlightAirport,
  FlightDetails,
  FlightLeg,
  FlightLookupDirection,
  GetFlightDetailsArgs,
  RawFlight,
  RawFlightAirport,
  RawFlightLeg,
} from "./types";

const toAirport = (airport: RawFlightAirport | null): FlightAirport | null =>
  airport && {
    name: airport.name,
    iata: airport.iata,
    icao: airport.icao,
    countryCode: airport.country_code,
    city: airport.city,
    timezone: airport.timezone,
  };

const toLeg = (leg: RawFlightLeg | null | undefined): FlightLeg | null =>
  leg
    ? {
        terminal: leg.terminal ?? null,
        baggage: leg.baggage ?? null,
        scheduled: leg.scheduled ?? null,
        estimated: leg.estimated ?? null,
        actual: leg.actual ?? null,
        gateScheduled: leg.gate_scheduled ?? null,
        gateEstimated: leg.gate_estimated ?? null,
        gateActual: leg.gate_actual ?? null,
        airport: toAirport(leg.airport ?? null),
      }
    : null;

/** Normalise the shared Flight DTO (snake_case) to camelCase. Exported so the guest flight twin
 * (guestApi.getGuestFlightDetails), which hits the same DTO on a different route, reuses it. */
export const toFlightDetails = (raw: RawFlight): FlightDetails => ({
  number: raw.number,
  operatingAs: raw.operating_as,
  status: raw.status,
  luggage: raw.luggage ?? null,
  delay: raw.delay ?? 0,
  lastUpdated: raw.last_updated ?? null,
  codeShare: raw.code_share ?? null,
  airline: raw.airline
    ? {
        name: raw.airline.name,
        iata: raw.airline.iata,
        icao: raw.airline.icao,
        callSign: raw.airline.call_sign,
        avatar: raw.airline.avatar ?? null,
      }
    : null,
  departure: toLeg(raw.departure),
  arrival: toLeg(raw.arrival),
  materialityThresholdMinutes: raw.materiality_threshold_minutes ?? null,
  departureLeadMinutes: raw.departure_lead_minutes ?? null,
  departureTravelSeconds: raw.departure_travel_seconds ?? null,
  recommendedPickupAt: raw.recommended_pickup_at ?? null,
});

/**
 * The departing-only query params, or nothing at all.
 *
 * Shared by the client and guest lookups so an ARRIVING request stays byte-identical on both routes:
 * `direction` is the server's own default, and adding it (or a stray `pickup_lat`) would change every
 * existing request and fork the cache entry the date panel and the sheet deliberately share.
 */
export const departureLookupParams = ({
  direction,
  pickupLat,
  pickupLng,
}: {
  direction?: FlightLookupDirection;
  pickupLat?: number;
  pickupLng?: number;
}): Record<string, string | number> =>
  direction === "departing"
    ? {
        direction,
        ...(pickupLat != null && pickupLng != null ? { pickup_lat: pickupLat, pickup_lng: pickupLng } : {}),
      }
    : {};

export const clientFlightApi = createApi({
  reducerPath: "clientFlightApi",
  baseQuery: baseQueryWithClient("flight"),
  endpoints: (builder) => ({
    getFlightDetails: builder.query<FlightDetails, GetFlightDetailsArgs>({
      query: ({ flightNumber, date, airport, accountId, ...direction }) => ({
        url: "",
        accountId,
        params: { flight_number: flightNumber, date, ...(airport ? { airport } : {}), ...departureLookupParams(direction) },
      }),
      transformResponse: toFlightDetails,
    }),
  }),
});

export const { useGetFlightDetailsQuery } = clientFlightApi;
